'use client';
import React, { useEffect, useRef, useState } from 'react';
import Typography from '@/components/atoms/typography';
import Checkbox from '@/components/atoms/checkbox';
import InputField from '@/components/atoms/floating-input';
import SelectField from '@/components/atoms/floating-select';
import MultiSelect from '@/components/atoms/multi-select';
import Button from '@/components/atoms/button';
import useAccount from '@/lib/hooks/useAccount';
import Link from 'next/link';
import useSwrClearCache from '@/hooks/useSwrClearCache';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import useCustomRouter from '@/hooks/useCustomRouter';
import Modal from '../modal';
import { useParams } from 'next/navigation';
import { sdk } from '@/sdk';
import Markdown from '@/components/atoms/markdown';

const companyTypes = [
    { value: 'Commercial Maintenance', name: 'Commercial Maintenance' },
    { value: 'Domestic Maintenance & Renovation', name: 'Domestic Maintenance & Renovation' },
    { value: 'Commercial Projects', name: 'Commercial Projects' },
    { value: 'Multi Residential Projects', name: 'Multi Residential Projects' },
    { value: 'Vol. Home Builder Projects', name: 'Vol. Home Builder Projects' },
    { value: 'TAFE', name: 'TAFE' },
];

const roles = [
    { value: 'Plumber', label: 'Plumber' },
    { value: 'Hydraulic Engineer', label: 'Hydraulic Engineer' },
    { value: 'Other', label: 'Other' },
  ];
  
const countries = [
    { value: 'au', label: 'Australia' },
    { value: 'nz', label: 'New Zealand' },
];

const auStates = [
    { value: 'act', label: 'ACT' },
    { value: 'nsw', label: 'NSW' },
    { value: 'nt', label: 'NT' },
    { value: 'qld', label: 'QLD' },
    { value: 'sa', label: 'SA' },
    { value: 'tas', label: 'TAS' },
    { value: 'vic', label: 'VIC' },
    { value: 'wa', label: 'WA' },
];

const nzStates = [
    { value: 'NorthIsland', label: 'North Island' },
    { value: 'SouthIsland', label: 'South Island' },
];

const size_of_business = [
  { value: '1', label: '1' },
  { value: '2-5', label: '2-5' },
  { value: '6-10', label: '6-10' },
  { value: '11-20', label: '11-20' },
  { value: '20+', label: '20+' },
];

const myroles = [
    { value: 'Apprentice Employee', label: 'Apprentice Employee' },
    { value: 'Employee', label: 'Employee' },
    { value: 'Owner', label: 'Owner' },
];


type FormData = {
    firstName: string;
    lastName: string;
    email: string;
    company: string;
    phone: string;
    companyType: string[];
    role: string;
    street: string;
    suburb: string;
    postalCode: string;
    myrole: string;
    country: string;
    state: string;
    termsAccepted: boolean;
    marketingAccepted: string; // 'true' | 'false'
    size_of_business: string;
};

const initialForm: FormData = {
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    companyType: [],
    role: '',
    myrole: '',
    street: '',
    suburb: '',
    postalCode: '',
    country: '',
    state: '',
    termsAccepted: false,
    marketingAccepted: 'false',
    size_of_business: ''
};

const CreateProfile = () => {
    const { account, logout, updateAccount } = useAccount();

    const router = useCustomRouter();
    const clearCache = useSwrClearCache();
    const [formData, setFormData] = useState<FormData>(initialForm);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [termsData, setTermsData] = useState<any>(null);
    const [termsLoading, setTermsLoading] = useState(false);
    const [hasReadTerms, setHasReadTerms] = useState(false);
    const termsScrollRef = useRef<HTMLDivElement>(null);
    const [count, setCount] = useState(5);
    const [error, setError] = useState<string | null>(null);
    const { locale } = useParams()

    const [touched, setTouched] = useState<Record<keyof FormData, boolean>>(
        Object.keys(initialForm).reduce(
            (acc, key) => ({ ...acc, [key]: false }),
            {} as Record<keyof FormData, boolean>
        )
    );

    // Always use account.email for validation and display
    const emailValue = account?.email || '';

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, type, value, checked } = e.target as HTMLInputElement;
        if (name === 'email') return;

        if (name === 'termsAccepted') {
            setHasReadTerms(false);
            setIsTermsModalOpen(true);
            return;
        }

        if (name === 'phone') {
            const numericValue = value.replace(/\D/g, '');
            if (numericValue.length > 12) return;
            setFormData(prev => ({
                ...prev,
                phone: numericValue,
            }));
            return;
        }

        setFormData(prev => {
            if (name === 'country') {
                const states = value === 'au' ? auStates :
                    value === 'nz' ? nzStates : [];

                if (prev.country !== value) {
                    return {
                        ...prev,
                        country: value,
                        state: '',
                    };
                }
            }

            return {
                ...prev,
                [name]: type === 'checkbox'
                    ? name === 'marketingAccepted' ? (checked ? 'true' : 'false') : checked
                    : value,
            };
        });
    };

    const handleBlur = (field: keyof FormData) => {
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    const handleTermsAgree = () => {
        setFormData(prev => ({ ...prev, termsAccepted: true }));
        setIsTermsModalOpen(false);
        setHasReadTerms(false);
    };

    const handleTermsDisagree = () => {
        setFormData(prev => ({ ...prev, termsAccepted: false }));
        setIsTermsModalOpen(false);
        setHasReadTerms(false);
    };

    const handleCompanyTypeChange = (vals: string[]) => {
        setFormData(prev => ({ ...prev, companyType: vals }));
        setTouched(prev => ({ ...prev, companyType: true }));
    };

    const isError = (field: keyof FormData) => {
        if (field === 'email') {
            return touched.email && !emailValue;
        }
        if (field === 'companyType') {
            return touched.companyType && formData.companyType.length === 0;
        }
        if (field === 'marketingAccepted') {
            return touched.marketingAccepted && formData.marketingAccepted !== 'true';
        }
        return touched[field] && !formData[field];
    };

    const mandatoryFields: (keyof FormData)[] = [
        'firstName',
        'lastName',
        'email',
        'company',
        'phone',
        'companyType',
        'role',
        'street',
        'suburb',
        'postalCode',
        'myrole',
        'country',
        'state',
        'termsAccepted',
        'marketingAccepted',
    ];

    const isFormIncomplete = mandatoryFields.some(field => {
        if (field === 'email') {
            return !emailValue;
        }
        if (field === 'companyType') {
            return formData.companyType.length === 0;
        }
        if (field === 'marketingAccepted') {
            return formData.marketingAccepted !== 'true';
        }
        return !formData[field];
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // simple validation
        const hasError = mandatoryFields.some(field => {
            if (field === 'email') {
                return !emailValue;
            }
            if (field === 'companyType') {
                return formData.companyType.length === 0;
            }
            if (field === 'marketingAccepted') {
                return formData.marketingAccepted !== 'true';
            }
            return !formData[field];
        });

        if (hasError) {
            setTouched(prev =>
                Object.keys(prev).reduce(
                    (acc, key) => ({ ...acc, [key]: true }),
                    {} as Record<keyof FormData, boolean>
                )
            );
            return;
        }
        const onboardingWorkflowStatus = "MemberRegistered";
        const now = new Date();

        const onboardingUpdatedDatetime = [
            String(now.getDate()).padStart(2, '0'),
            String(now.getMonth() + 1).padStart(2, '0'),
            now.getFullYear(),
        ].join('-') + ' ' +
            [
                String(now.getHours()).padStart(2, '0'),
                String(now.getMinutes()).padStart(2, '0'),
                String(now.getSeconds()).padStart(2, '0'),
            ].join(':');

        const response = await updateAccount({
            ...formData,
            email: emailValue,
            companyType: formData.companyType.join(','),
            customer_marketing_consent: formData.marketingAccepted === 'true',
            onboarding_workflow_status: onboardingWorkflowStatus,
            onboarding_updated_datetime:
                onboardingWorkflowStatus === "MemberRegistered"
                    ? onboardingUpdatedDatetime
                    : account?.onboarding_updated_datetime,
        });
        if (response && response?.accountId) {
            setIsModalOpen(true);
            handleRedirect(formData.country);
            console.log("Update successful", response);
        } else {
            setIsModalOpen(true);
            setError("Something went wrong, please try again.");
        }

    };

    // Dynamically select states based on country
    const stateOptions =
        formData.country === 'nz' ? nzStates :
            formData.country === 'au' ? auStates :
                [];

    const onLogoutClick = () => {
        logout().then(() => {
            router.push('/login');
            router.refresh();
            clearCache();
        });
    };

    useEffect(() => {
        const body = document.body;
        if (isModalOpen) {
            body.style.overflow = 'hidden';
            body.classList.add('modal-open');
        } else {
            body.style.overflow = '';
            body.classList.remove('modal-open');
        }
        return () => {
            body.style.overflow = '';
            body.classList.remove('modal-open');
        };
    }, [isModalOpen]);

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    useEffect(() => {
        if (!isTermsModalOpen || termsData) return;

        let cancelled = false;
        const load = async () => {
            setTermsLoading(true);
            try {
                sdk.defaultConfigure(locale as string);
                const res: any = await sdk.page.getPage({ path: '/terms-of-use' });
                if (cancelled || res?.isError) return;

                const pageData = res?.data;
                const dataSources = pageData?.data?.dataSources ?? {};
                const sections = [
                    pageData?.page?.sections?.head,
                    pageData?.page?.sections?.main,
                    pageData?.page?.sections?.footer,
                ].filter(Boolean);

                let config: any;
                for (const section of sections) {
                    for (const layoutElement of section?.layoutElements ?? []) {
                        for (const tastic of layoutElement?.tastics ?? []) {
                            if (tastic?.tasticType === 'commercetools/ui/content/terms-of-use') {
                                config = tastic?.configuration;
                                break;
                            }
                        }
                        if (config) break;
                    }
                    if (config) break;
                }

                const dataSourceId = config?.data?.dataSourceId;
                const ds = dataSourceId ? dataSources?.[dataSourceId] : undefined;
                if (ds && !cancelled) setTermsData(ds);
            } finally {
                if (!cancelled) setTermsLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [isTermsModalOpen, termsData, locale]);

    // Auto-mark as read if content doesn't need scrolling
    useEffect(() => {
        if (!isTermsModalOpen || !termsData || hasReadTerms) return;
        const el = termsScrollRef.current;
        if (el && el.scrollHeight <= el.clientHeight + 10) {
            setHasReadTerms(true);
        }
    }, [isTermsModalOpen, termsData, hasReadTerms]);

    const handleTermsScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (hasReadTerms) return;
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop <= el.clientHeight + 50) {
            setHasReadTerms(true);
        }
    };


    const handleRedirect = async (country: string) => {
        let currentCount = count;
        const interval = setInterval(() => {
            currentCount -= 1;
            setCount(currentCount);
            if (currentCount <= 1) {
                clearInterval(interval);
                const redirectUrl = country === 'au' ? '/au' : country === 'nz' ? '/nz' : '/';
                window.location.href = redirectUrl;
                setIsModalOpen(true);
            }
        }, 1000);
    };

    // useEffect(() => {
    //     if (account) {
    //         const countryFromLocale = locale === 'au' ? 'au' : locale === 'nz' ? 'nz' : (account.country || '');
    //         const states = countryFromLocale === 'au' ? auStates : 
    //                      countryFromLocale === 'nz' ? nzStates : [];
    //         const defaultState = states.length > 0 ? states[0].value : '';

    //         setFormData(prev => ({
    //             ...prev,
    //             firstName: account.firstName ?? '',
    //             lastName: account.lastName ?? '',
    //             email: account.email ?? '',
    //             company: account.companyName ?? '',
    //             phone: account.phone ?? '',
    //             companyType: account.company_type ?? '',
    //             role: account.role ?? '',
    //             country: countryFromLocale,
    //             state: account.state ?? '',
    //         }));
    //     }
    // }, [account, locale]);

    useEffect(() => {
        if (account) {
            // Debug logs for all relevant fields
            console.log('Create Profile - Account data:', {
                company_type: {
                    raw: account?.company_type,
                    normalized: account?.company_type?.toLowerCase(),
                    match: companyTypes.find(opt => opt.value === account?.company_type?.toLowerCase()),
                    availableOptions: companyTypes
                },
                role: {
                    raw: account?.role,
                    normalized: account?.role?.toLowerCase(),
                    match: roles.find(opt => opt.value === account?.role?.toLowerCase())
                },
                country: {
                    raw: account?.country,
                    normalized: account?.country?.toLowerCase(),
                    match: countries.find(opt => opt.value === account?.country?.toLowerCase())
                },
                state: {
                    raw: account?.state,
                    normalized: account?.state?.toLowerCase(),
                    match: [...auStates, ...nzStates].find(opt => opt.value.toLowerCase() === account?.state?.toLowerCase())
                }
            });
            
            // Debug the SelectField value setting
            const companyTypeValue = account?.company_type?.toLowerCase() || '';

            // Determine country based on locale or account data
            const countryValue = locale === 'au' ? 'au' :
                locale === 'nz' ? 'nz' :
                    account.country?.toLowerCase() ?? '';
            
                    const normalizeNZState = (value?: string) => {
                if (!value) return '';

                const val = value.toLowerCase();
                if (val === 'northisland') return 'NorthIsland';
                if (val === 'southisland') return 'SouthIsland';
                return value;
            };
            
            setFormData(prev => ({
                ...prev,
                firstName: account.firstName ?? '',
                lastName: account.lastName ?? '',
                email: account.email ?? '',
                company: account.companyName ?? '',
                phone: account.phone ?? '',
                companyType: account?.company_type
                    ? account.company_type.split(',').map((s: string) => s.trim()).filter(Boolean)
                    : [],
                role: account.role ?? '',
                myrole: account.myrole ?? '',                
                street: account.street ?? '',
                suburb: account.suburb ?? '',
                postalCode: account.postalCode ?? '',
                country: countryValue,
                state: normalizeNZState(account?.state) ?? '',
                termsAccepted: account.termsAccepted ?? false,
                size_of_business: account.size_of_business ?? '',
                marketingAccepted:
                    account?.customer_marketing_consent === true ||
                    (typeof account?.customer_marketing_consent === 'string' && account.customer_marketing_consent === 'true')
                        ? 'true'
                        : 'false',
            }));
        }
    }, [account, locale]);
    return (
        <div className="min-h-screen flex flex-col bg-[#F3EDE9] py-8 px-4 lg:px-60">
            <Typography as="h1" className="text-32 font-medium mb-6 text-gray-900">
                Create My Profile
            </Typography>
            <div className="w-full rounded-lg shadow-md p-6 bg-white">
                <form
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    onSubmit={handleSubmit}
                    noValidate
                >
                    {/* Reusable Input */}
                    <div className=''>
                        <InputField
                            name="firstName"
                            label="First Name*"
                            value={formData.firstName}
                            onChange={handleChange}
                            onBlur={() => handleBlur('firstName')}
                            error={isError('firstName')}
                        />
                        {isError('firstName') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>
                    <div className="div">
                        <InputField
                            name="lastName"
                            label="Last Name*"
                            value={formData.lastName}
                            onChange={handleChange}
                            onBlur={() => handleBlur('lastName')}
                            error={isError('lastName')}
                        />
                        {isError('lastName') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>
                    <div className="div">
                        <InputField
                            name="email"
                            label="Email Address*"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={() => handleBlur('email')}
                            error={isError('email')}
                            disabled={true}
                              
                        />
                        {isError('email') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>
                    <div className="div">
                        <InputField
                            name="phone"
                            label="Phone Number*"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            onBlur={() => handleBlur('phone')}
                            error={isError('phone')}
                              
                        />
                        {isError('phone') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>
                    <div className="div">
                        <InputField
                            name="company"
                            label="Company*"
                            value={formData.company}
                            onChange={handleChange}
                            onBlur={() => handleBlur('company')}
                            error={isError('company')}
                              
                        />
                        {isError('company') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>
                    <div className="div">
                        <MultiSelect
                            label="Company Type*"
                            options={companyTypes}
                            value={formData.companyType}
                            onChange={handleCompanyTypeChange}
                            className='text-base'
                            required
                        />
                        {isError('companyType') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>
                    <div className="div">
                        <SelectField
                            name="role"
                            label="My Trade*"
                            options={roles}
                            value={formData.role}
                            onChange={handleChange}
                            onBlur={() => handleBlur('role')}
                            error={isError('role')}
                            className='text-base'
                        />
                        {isError('role') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>                    
                    <div className="div">
                        <SelectField
                            name="myrole"
                            label="My Role*"
                            options={myroles}
                            value={formData.myrole}
                            onChange={handleChange}
                            onBlur={() => handleBlur('myrole')}
                            error={isError('myrole')}
                            className='text-base'
                        />
                        {isError('myrole') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>
                    {/* yearsofexperience - hidden */}
                    <div>
                        <SelectField
                            name="size_of_business"
                            label="Size of Business*"
                            options={size_of_business}
                            value={formData.size_of_business}
                            onChange={handleChange}
                            onBlur={() => handleBlur('size_of_business')}
                            error={isError('size_of_business')}
                            className='!max-h-60'
                        />
                        {isError('size_of_business') && (
                            <span className="text-red-500 text-xs mt-1">Required</span>
                        )}
                    </div>
                    <div className="div">
                        <InputField
                            name="street"
                            label="Address *"
                            value={formData.street}
                            onChange={handleChange}
                            onBlur={() => handleBlur('street')}
                            error={isError('street')}
                        />
                        {isError('street') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>
                    <div className="div">
                        <InputField
                            name="suburb"
                            label="Suburb *"
                            value={formData.suburb}
                            onChange={handleChange}
                            onBlur={() => handleBlur('suburb')}
                            error={isError('suburb')}
                        />
                        {isError('suburb') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>
                    <div className="div">
                        <InputField
                            name="postalCode"
                            label="Postcode *"
                            value={formData.postalCode}
                            onChange={handleChange}
                            onBlur={() => handleBlur('postalCode')}
                            error={isError('postalCode')}
                        />
                        {isError('postalCode') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>
                    <div className="div">
                        <SelectField
                            name="country"
                            label="Country*"
                            options={countries}
                            value={formData.country}
                            onChange={handleChange}
                            onBlur={() => handleBlur('country')}
                            error={isError('country')}
                            className='text-base'
                        />
                        {isError('country') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>
                    <div className="div">
                        <SelectField
                            name="state"
                            label="State*"
                            options={stateOptions}
                            value={formData.state}
                            onChange={handleChange}
                            onBlur={() => handleBlur('state')}
                            error={isError('state')}
                            className='text-base'
                        />
                        {isError('state') && (
                            <span className="text-red-500 text-16 mt-1">Required</span>
                        )}
                    </div>
                    {/* Terms + Marketing */}
                    <div className="md:col-span-2 space-y-3">
                        <div className="text-14 font-normal text-gray-600 gap-2">
                        By submitting this form, you are providing your personal information to GWA to collect, use, and disclose in accordance with GWA's <Link href={'/collection-statement'} target="_blank" className="font-semibold text-16">Collection Statement</Link> and <Link href={'/privacy/'} target="_blank" className=" text-16 font-semibold underline cursor-pointer">Privacy Policy</Link>. 
                        </div>
                        <div className="div">
                            <div className="flex items-start">
                                <Checkbox
                                    name="termsAccepted"
                                    checked={formData.termsAccepted}
                                    onChange={handleChange}
                                    required
                                    label={
                                        <span className="text-14">
                                            I've read and accepted the Caroma Plumbers' Hub <button type="button" onClick={() => setIsTermsModalOpen(true)} className="font-semibold text-gray-600 text-16 underline cursor-pointer">Terms and Conditions.</button>
                                        </span>
                                    }
                                    error={isError('termsAccepted')}
                                    className={
                                        formData.termsAccepted
                                            ? 'bg-black !text-black !border-black text-16 font-normal'
                                            : '!bg-white !text-black !border-black text-16 font-normal'
                                    }
                                />
                            </div>
                            {isError('termsAccepted') && (
                                <span className="text-red-500 text-16 mt-1">Required</span>
                            )}
                        </div>
                    </div>

                    <div className="md:col-span-2 space-y-3">
                        <div className="div">
                            <div className="flex items-start">
                                <Checkbox
                                    name="marketingAccepted"
                                    checked={formData.marketingAccepted === 'true'}
                                    onChange={handleChange}
                                    required
                                    label={
                                        <span className="text-14">
                                            I agree to receive marketing communications from Caroma and other members of the GWA Group, including training content, technical specification information, product updates, event invitations and promotional offers. I understand I can withdraw my consent and unsubscribe at any time including by <Link href="mailto:GWAPrivacy@gwagroup.com.au" className="font-semibold underline cursor-pointer ![color:rgb(0,0,255)]">contacting GWA.</Link>
                                        </span>
                                    }
                                    error={isError('marketingAccepted')}
                                    className={
                                        formData.marketingAccepted === 'true'
                                            ? 'bg-black !text-black !border-black text-16 font-normal'
                                            : '!bg-white !text-black !border-black text-16 font-normal'
                                    }
                                />
                            </div>
                            {isError('marketingAccepted') && (
                                <span className="text-red-500 text-16 mt-1">Required</span>
                            )}
                        </div>
                    </div>


                    <Button
                        variant='ghost'
                        type="submit"
                        className="mt-6 bg-black !w-fit !px-4 cursor-pointer text-white text-16 font-medium py-[10px] rounded hover:bg-gray-800 transition"
                        // disabled={isFormIncomplete}
                        style={{ width: 'fit-content' }}
                    >
                        Complete Registration
                    </Button>
                </form>
                <div className="mt-4 text-14 text-gray-500 mb-5">
                    Not your email address?{' '}
                    <span className="font-semibold text-gray-600 cursor-pointer" onClick={onLogoutClick}>
                        Click here
                    </span>{' '}
                    to logout.
                </div>
            </div>
            <Modal
                className="flex flex-col items-center justify-center !p-10"
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                preventScroll={false}
                closeButton={true}
                size="sm"
                variant="default"
                centered={true}
            >
                {error ? (
                    <div className="text-center text-16 !font-medium text-black">
                        {error}
                    </div>
                ) : (
                    <>
                        <div className="text-center text-16 !font-medium text-black">
                            {"My Profile has been created Successfully"}
                        </div>
                        <div className="text-center text-16 !font-medium text-black">
                            {"Redirecting to homepage in "}{count}{" seconds"}
                        </div>
                    </>
                )}




            </Modal>

            <Modal
                isOpen={isTermsModalOpen}
                onRequestClose={() => { setIsTermsModalOpen(false); setHasReadTerms(false); }}
                closeButton={true}
                size="lg"
                centered={true}
                className="overflow-hidden flex flex-col"
            >
                <div
                    ref={termsScrollRef}
                    onScroll={handleTermsScroll}
                    className="flex-1 overflow-y-auto pt-10 px-4 sm:px-8 pb-4"
                >
                    {termsLoading && (
                        <p className="text-center text-gray-500 py-8">Loading...</p>
                    )}
                    {!termsLoading && termsData && (
                        <>
                            {termsData?.sections?.[0]?.section_with_heading_description?.title && (
                                <Typography as="h2" className="text-24 font-medium text-gray-800 mb-4">
                                    {termsData.sections[0].section_with_heading_description.title}
                                </Typography>
                            )}
                            <Markdown className="text-14 text-primary-black [&>p]:my-3 [&>ul]:list-disc [&>ul]:ml-6 [&>ul>li]:my-2 [&>p>a]:text-blue-600 [&>p>a]:underline">
                                {termsData?.sections?.[0]?.section_with_heading_description?.description ?? ''}
                            </Markdown>
                        </>
                    )}
                    {!termsLoading && !termsData && (
                        <p className="text-center text-gray-500 py-8">Unable to load Terms and Conditions.</p>
                    )}
                </div>
                <div className="flex-shrink-0 border-t border-gray-200 px-4 sm:px-8 py-4 text-center">
                    {!hasReadTerms ? (
                        <p className="text-14 text-gray-500 italic">Please scroll to the bottom to read and accept terms.</p>
                    ) : (
                        <div className="flex justify-center gap-4">
                            <button
                                type="button"
                                onClick={handleTermsAgree}
                                className="px-6 py-2 bg-black text-white text-14 font-medium rounded hover:bg-gray-800 transition cursor-pointer"
                            >
                                Agree
                            </button>
                            <button
                                type="button"
                                onClick={handleTermsDisagree}
                                className="px-6 py-2 border border-gray-400 text-gray-700 text-14 font-medium rounded hover:bg-gray-100 transition cursor-pointer"
                            >
                                Disagree
                            </button>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default CreateProfile;
